import React from "react";
import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";
import {
  Bookmark as LabelIcon,
  AddBox as FieldIcon,
} from "@mui/icons-material";
import { Priority } from "hooks/usePriorities";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import GoogleDriveMenu from "components/menus/GoogleDriveMenu";
import LabelManager from "../labels/LabelManager";
import { getTranslation } from "common";

interface MobileDetailToolbarProps {
  priorities: Priority[];
  selectedPriority: Priority | null;
  onPriorityChange: (priority: Priority) => void;
  onOpenLabels: () => void;
  onOpenDrive: (event: React.MouseEvent<HTMLElement>) => void;
  onAddField: () => void;
  isEditable: boolean;
  translations: Record<string, string>;
  /** For GoogleDriveMenu anchorEl — when null, the menu is closed. */
  driveAnchorEl: HTMLElement | null;
  onDriveClose: () => void;
  /** For LabelManager — when true the manager is open. */
  isLabelManagerOpen: boolean;
  onLabelManagerClose: () => void;
  /** ID del processo per i menu figli. */
  idprocess?: number;
}

/**
 * Horizontal scrollable toolbar shown above the tab bar in the mobile task detail.
 * Contains: priority select, Labels, Drive, Add Field. Mirrors the mockup's
 * horizontally-scrollable toolbar with pill-style controls.
 */
const MobileDetailToolbar: React.FC<MobileDetailToolbarProps> = ({
  priorities,
  selectedPriority,
  onPriorityChange,
  onOpenLabels,
  onOpenDrive,
  onAddField,
  isEditable,
  translations,
  driveAnchorEl,
  onDriveClose,
  isLabelManagerOpen,
  onLabelManagerClose,
  idprocess,
}) => {
  const priorityLabel = getTranslation(translations, "generic.priorities.caption");
  const labelsLabel = getTranslation(translations, "generic.labels.caption");
  const driveLabel = "Drive";
  const addFieldLabel = getTranslation(translations, "pm.mayactivities.addfield.label");

  return (
    <>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          overflowX: "auto",
          overflowY: "hidden",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: "max-content", pb: 0.25 }}>
          {/* Priority select (always visible so users can read the current priority) */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "grey.50"),
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              px: 1,
              py: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: "text.secondary" }}>
              {priorityLabel}:
            </Typography>
            <Select
              id="mobile-priority-select"
              size="small"
              value={selectedPriority?.id ?? ""}
              onChange={(e) => {
                const p = priorities.find((x) => x.id === Number(e.target.value));
                if (p) onPriorityChange(p);
              }}
              disabled={!isEditable || priorities.length === 0}
              variant="standard"
              disableUnderline
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: "text.primary",
                minWidth: 60,
                "& .MuiSelect-select": { py: 0, pr: 1.5, pl: 0.5 },
              }}
            >
              {priorities.map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ fontSize: 11 }}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          {isEditable && (
            <>
              <FlowentiButton
                id="mobile-toolbar-labels"
                size="small"
                variant="outlined"
                startIcon={<LabelIcon sx={{ fontSize: 12 }} />}
                onClick={onOpenLabels}
                sx={{ fontSize: 10, py: 0.5, px: 1, borderRadius: 3, flexShrink: 0 }}
              >
                {labelsLabel}
              </FlowentiButton>

              <FlowentiButton
                id="mobile-toolbar-drive"
                size="small"
                variant="outlined"
                onClick={onOpenDrive}
                sx={{ fontSize: 10, py: 0.5, px: 1, borderRadius: 3, flexShrink: 0 }}
              >
                {driveLabel}
              </FlowentiButton>

              <FlowentiButton
                id="mobile-toolbar-addfield"
                size="small"
                variant="outlined"
                startIcon={<FieldIcon sx={{ fontSize: 12 }} />}
                onClick={onAddField}
                sx={{ fontSize: 10, py: 0.5, px: 1, borderRadius: 3, flexShrink: 0 }}
              >
                {addFieldLabel}
              </FlowentiButton>
            </>
          )}
        </Stack>
      </Box>

      {idprocess !== undefined && (
        <>
          <GoogleDriveMenu
            anchorEl={driveAnchorEl}
            open={Boolean(driveAnchorEl)}
            onClose={onDriveClose}
            onRefreshAttachments={onDriveClose}
            organizationId={idprocess}
          />
          <LabelManager
            idprocess={idprocess}
            open={isLabelManagerOpen}
            handleClose={onLabelManagerClose}
          />
        </>
      )}
    </>
  );
};

export default MobileDetailToolbar;